import type { YieldFetcher } from '../types'
import { LST_REGISTRY } from '../../vaults/lst/registry'
import { SAVINGS_REGISTRY } from '../../vaults/savings/registry'

// Lending-only intrinsic fetchers.
//
// These cover assets whose intrinsic yield is surfaced ONLY through the
// global intrinsic map (lending-market collateral injection via
// `AdditionalYields.intrinsicYields`, and the `/yields/intrinsic`
// endpoint) — they are not wired into an LST or savings vault provider.
//
// Every LST/savings fetcher is contributed AUTOMATICALLY by the vault
// registries below (`collectVaultFetchers`), so those must NOT be listed
// here — that is the whole point of the single-source union: a fetcher
// referenced by a vault entry can never be missing from the global map,
// and vice-versa. If you add a vault entry with a new `yieldFetcher`, it
// lands in the global map for free; only genuinely lending-only sources
// belong in this list.
//
// `weETH` is intentionally absent — it is composed at fetch time from the
// wstETH result (`createWeethFetcher`) in `fetchIntrinsicYields`, so it
// cannot be a plain array entry.
import { stmaticFetcher } from './fetchers/lido'
import { weethsFetcher } from './fetchers/etherfi'
import { csusdlFetcher } from './fetchers/morphoVaults'
import { usdZeroPlusFetcher, ethZeroFetcher } from './fetchers/usual'
import { jitoSolFetcher } from './fetchers/jito'
import { thbillFetcher } from './fetchers/theo'
import { ssuperusdFetcher } from './fetchers/superreturn'
import { hlpFetcher, hwhlpFetcher } from './fetchers/hyperliquid'
import { susdxFetcher } from './fetchers/usdx'
import { rtokensFetcher } from './fetchers/reserve'
import { rlpFetcher } from './fetchers/resolv'
import { gmxFetcher } from './fetchers/gmx'
import { pendleFetcher } from './fetchers/pendle'
import { usdaiFetcher } from './fetchers/usdai'
import { ondoFetcher } from './fetchers/ondo'
import { syzusdFetcher } from './fetchers/yuzu'
import { midasFetcher } from './fetchers/midas'
import { nestFetcher } from './fetchers/nest'
import { accountableFetcher } from './fetchers/accountable'
import { upshiftFetcher } from './fetchers/upshift'
import { yearnFetcher } from './fetchers/yearn'
import { lsethFetcher } from './fetchers/liquidCollective'
import { susdzFetcher } from './fetchers/anzen'
import { lisAsterFetcher } from './fetchers/lista'

/** Fetchers surfaced only via the global intrinsic map (no vault provider). */
export const LENDING_ONLY_FETCHERS: YieldFetcher[] = [
  stmaticFetcher,
  weethsFetcher,
  csusdlFetcher,
  usdZeroPlusFetcher,
  ethZeroFetcher,
  jitoSolFetcher,
  thbillFetcher,
  // sthusdFetcher / scrvusdFetcher are NOT here (and deliberately not
  // imported): both are vault-wired by their savings rows, so they reach
  // the global map through `collectVaultFetchers` (the disjointness rule
  // above). thbillFetcher stays — thBILL is a lending collateral with no
  // vault row of its own.
  ssuperusdFetcher,
  hlpFetcher,
  hwhlpFetcher,
  susdxFetcher,
  rtokensFetcher,
  rlpFetcher,
  gmxFetcher,
  pendleFetcher,
  usdaiFetcher,
  ondoFetcher,
  // rwaFundsFetcher is NOT here: cUSDO's savings rows vault-wire it, so
  // it reaches the global map via `collectVaultFetchers` (disjointness rule).
  syzusdFetcher,
  midasFetcher,
  // capFetcher is NOT here: the Cap stcUSD savings row vault-wires it, so it
  // reaches the global map via `collectVaultFetchers` (disjointness rule).
  // It was double-listed, which the registry test flags — the union dedupes
  // by reference, so removing it here changes nothing at runtime.
  nestFetcher,
  accountableFetcher,
  // slisBnbFetcher is NOT here (LST-wired); lisAster has no vault row — it
  // is a lock receipt with no rate and no exit — so its reward stream is
  // lending-only, surfaced for the Moolah lisAster/ASTER collateral join.
  lisAsterFetcher,
  upshiftFetcher,
  // Every endorsed Yearn V3 vault on every Yearn chain (Katana included —
  // it superseded the Katana-only fetcher, which double-counted).
  yearnFetcher,
  // DefiLlama-mapped intrinsic yields for classifier assets lacking a bespoke source
  lsethFetcher,
  // tethFetcher / tavaxFetcher are NOT here: the Treehouse tAsset LST rows
  // vault-wire them, so they reach the global map via `collectVaultFetchers`
  // (disjointness rule).
  // strataFetcher is NOT here: the Strata tranche savings rows vault-wire
  // it, so it reaches the global map via `collectVaultFetchers`.
  susdzFetcher,
]

/**
 * Every `YieldFetcher` referenced by an LST or savings vault registry
 * entry, deduped by reference. Registry entries can point many rows at
 * one fetcher (Maple syrup* → `mapleFetcher`, Angle stUSD/stEUR →
 * `angleFetcher`, Sky sUSDS/stUSDS → `susdsFetcher`), so the same ref
 * appears repeatedly across chains — the `Set` collapses them.
 *
 * This is the mechanism that keeps the vault providers and the global
 * intrinsic map in lockstep: the global union derives its LST/savings
 * slice from here rather than a hand-maintained parallel list.
 */
export const collectVaultFetchers = (): YieldFetcher[] => {
  const seen = new Set<YieldFetcher>()
  for (const registry of [LST_REGISTRY, SAVINGS_REGISTRY]) {
    for (const entries of Object.values(registry)) {
      for (const e of entries) {
        if (e.yieldFetcher) seen.add(e.yieldFetcher)
      }
    }
  }
  return [...seen]
}

/**
 * The single canonical set of intrinsic-yield fetchers: the union of
 * every vault-registry fetcher and the lending-only extras, deduped by
 * reference. `fetchIntrinsicYields` runs this; the vault bindings run
 * their per-entry subset of the *same* fetcher objects — so there is one
 * source of truth and no drift.
 */
export const INTRINSIC_FETCHERS: YieldFetcher[] = (() => {
  const seen = new Set<YieldFetcher>()
  for (const f of [...collectVaultFetchers(), ...LENDING_ONLY_FETCHERS]) {
    seen.add(f)
  }
  return [...seen]
})()
